import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { billingService } from '../../services/billingService';
import { formatRub, PLAN_TITLES } from '../../lib/billing';
import { logUiEvent } from '../../services/uiLogger';
import { HEAD_ROW, TH, TD, ROW, SELECT, BADGE, TITLE, HINT, EMPTY, ERROR_BOX, ACCENT_BTN } from './adminStyles';

const PAGE_SIZE = 20;

// Метки и бейджи статусов заявки; допустимые действия — зеркало TRANSITIONS
// в bit-billing/app/invoices.py (activated — только через POST /activate).
const STATUS = {
  new: { label: 'Новая', badge: 'bg-paper-bg text-paper-ink-2' },
  invoiced: { label: 'Счёт выставлен', badge: 'bg-paper-panel text-paper-warning' },
  paid: { label: 'Оплачена', badge: 'bg-emerald-100 text-emerald-700' },
  activated: { label: 'Активирована', badge: 'bg-paper-panel text-paper-accent' },
  rejected: { label: 'Отклонена', badge: 'bg-paper-track text-paper-ink-3' },
};

const NEXT_ACTIONS = {
  new: [['Выставить счёт', 'invoiced']],
  invoiced: [['Отметить оплату', 'paid']],
  paid: [], // активация — отдельной кнопкой ниже
};

const PERIOD_LABEL = { month: 'месяц', year: 'год' };

export default function BillingTab() {
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      const data = await billingService.adminGetInvoiceRequests(params);
      setItems(data.items || []);
      setTotalCount(data.total_count || 0);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка загрузки заявок');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const mergeItem = (id, fields) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...fields } : it)));
  };

  const withBusy = async (id, fn) => {
    setBusyId(id);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.response?.data?.detail || 'Операция не удалась');
    } finally {
      setBusyId(null);
    }
  };

  const patchStatus = (item, status) => {
    if (status === 'rejected' && !window.confirm(`Отклонить заявку ${item.email}?`)) return;
    withBusy(item.id, async () => {
      const updated = await billingService.adminUpdateInvoiceRequest(item.id, { status });
      mergeItem(item.id, updated);
      logUiEvent('billing_status_change', { component: 'admin_billing', meta: { request_id: item.id, status } });
    });
  };

  const activate = (item) => {
    withBusy(item.id, async () => {
      try {
        const res = await billingService.adminActivate(item.id);
        mergeItem(item.id, { status: res.status, subscription_id: res.subscription_id });
      } catch (e) {
        // Заявку уже активировали (другой админ / повторный клик) — это успех, не ошибка
        const detail = e.response?.data?.detail || '';
        if (e.response?.status === 409 && detail.includes("текущий: 'activated'")) {
          mergeItem(item.id, { status: 'activated' });
        } else {
          throw e;
        }
      }
      logUiEvent('billing_activate', {
        component: 'admin_billing',
        meta: { request_id: item.id, plan_code: item.plan_code, period: item.billing_period },
      });
    });
  };

  const saveNote = (item) => {
    withBusy(item.id, async () => {
      const updated = await billingService.adminUpdateInvoiceRequest(item.id, {
        admin_note: noteDrafts[item.id] ?? '',
      });
      mergeItem(item.id, updated);
    });
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className={TITLE}>Заявки на счёт</h3>
        <select
          data-testid="status-filter"
          className={SELECT}
          value={statusFilter}
          onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
        >
          <option value="">Все статусы</option>
          {Object.entries(STATUS).map(([code, s]) => (
            <option key={code} value={code}>{s.label}</option>
          ))}
        </select>
        <span className={HINT}>{totalCount} шт.</span>
      </div>

      {error && <div className={`${ERROR_BOX} mb-3`}>{error}</div>}
      {loading && <div className={EMPTY}>Загрузка...</div>}

      {!loading && items.length === 0 && (
        <div className={EMPTY}>Заявок нет</div>
      )}

      {!loading && items.length > 0 && (
        <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={TH}>Дата</th>
              <th className={TH}>Клиент</th>
              <th className={TH}>Тариф</th>
              <th className={`${TH} text-right`}>Сумма</th>
              <th className={TH}>Статус</th>
              <th className={TH}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const st = STATUS[item.status] || { label: item.status, badge: 'bg-paper-bg text-paper-ink-2' };
              const busy = busyId === item.id;
              return (
                <React.Fragment key={item.id}>
                  <tr className={`${ROW} align-top`}>
                    <td className={`${TD} whitespace-nowrap text-paper-ink-3`}>
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('ru-RU') : '—'}
                    </td>
                    <td className={TD}>{item.email}</td>
                    <td className={`${TD} whitespace-nowrap`}>
                      {PLAN_TITLES[item.plan_code] || item.plan_code}
                      <span className="text-paper-ink-3"> · {PERIOD_LABEL[item.billing_period] || item.billing_period}</span>
                    </td>
                    <td className={`${TD} whitespace-nowrap text-right tabular-nums`}>{formatRub(item.amount_rub)}</td>
                    <td className={TD}>
                      <span className={`${BADGE} ${st.badge}`}>{st.label}</span>
                    </td>
                    <td className={TD}>
                      <div className="flex flex-wrap gap-1">
                        {(NEXT_ACTIONS[item.status] || []).map(([label, next]) => (
                          <Button key={next} variant="outline" size="sm" disabled={busy}
                            onClick={() => patchStatus(item, next)}>{label}</Button>
                        ))}
                        {item.status === 'paid' && (
                          <Button size="sm" className={ACCENT_BTN} disabled={busy}
                            onClick={() => activate(item)}>Активировать тариф</Button>
                        )}
                        {item.status in NEXT_ACTIONS && (
                          <Button variant="outline" size="sm" className="text-paper-accent" disabled={busy}
                            onClick={() => patchStatus(item, 'rejected')}>Отклонить</Button>
                        )}
                        <button className="text-xs text-paper-hint hover:text-paper-ink-2 px-1"
                          onClick={() => setExpanded(expanded === item.id ? null : item.id)}>Детали</button>
                      </div>
                    </td>
                  </tr>
                  {expanded === item.id && (
                    <tr className="border-b bg-paper-panel/50">
                      <td colSpan={6} className="px-3 py-3 text-xs text-paper-ink-2">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 max-w-2xl">
                          <div>Компания: {item.company_name}</div>
                          <div>ИНН: {item.inn}{item.kpp ? `, КПП: ${item.kpp}` : ''}</div>
                          <div>Телефон: {item.contact_phone || '—'}</div>
                          <div>Комментарий: {item.comment || '—'}</div>
                          {item.subscription_id && <div>Подписка: {item.subscription_id}</div>}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Input
                            data-testid={`admin-note-${item.id}`}
                            className="w-72 text-xs"
                            placeholder="Заметка для менеджера (№ счёта и т.п.)"
                            value={noteDrafts[item.id] ?? item.admin_note ?? ''}
                            onChange={(e) => setNoteDrafts((p) => ({ ...p, [item.id]: e.target.value }))}
                          />
                          <Button variant="outline" size="sm" disabled={busy}
                            onClick={() => saveNote(item)}>Сохранить заметку</Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
        </Card>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2 mt-3 text-sm">
          <button className="px-2 py-1 border rounded text-xs disabled:opacity-40"
            disabled={page <= 1} onClick={() => setPage(page - 1)}>←</button>
          <span className="text-xs text-paper-ink-3">стр. {page} из {totalPages}</span>
          <button className="px-2 py-1 border rounded text-xs disabled:opacity-40"
            disabled={page >= totalPages} onClick={() => setPage(page + 1)}>→</button>
        </div>
      )}
    </div>
  );
}
